'use client'
import {
  ButtonRoundStyle,
  Form,
  PopupWrapper,
  RHFAutocomplete,
  RHFDatePicker,
  RHFTextField,
} from '@/components'
import { INTERNAL_DATA } from '@/config/global-config'
import { requestUniversalOtp, updateInfoCustomer, verifyUniversalOtp } from '@/features/auth'
import { OTPCountDown } from '@/features/auth/components/otp-countdown'
import { useBanks } from '@/hooks/useBanks'
import { SetInitialPasswordForm } from '@/layouts/script-client/set-initial-password-form'
import { useAccountStore } from '@/store/accounts'
import yup from '@/utils/yup-global'
import { userSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Avatar, Box, DialogContent, DialogTitle, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import ChangePassword from './change-password'

const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export const InformationAccount = () => {
  const t = useTranslations('UserProfile')
  const { data, updateUser } = useAccountStore()
  const [isUpdateBankInfo, setIsUpdateBankInfo] = useState(false)
  // const [initialPhoneNumber, setInitialPhoneNumber] = useState('')
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [showOtpDialog, setShowOtpDialog] = useState(false)
  const [showPhoneDialog, setShowPhoneDialog] = useState(false)
  const [pendingData, setPendingData] = useState<yup.InferType<typeof userSchema> | null>(null)
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | null>(null)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [otpValue, setOtpValue] = useState('')
  const [newPhoneNumber, setNewPhoneNumber] = useState('')
  const isFirstChangePassword = data?.is_phone_registered
  const { bankOptions } = useBanks()
  const method = useForm<yup.InferType<typeof userSchema>>({
    resolver: yupResolver(userSchema),
  })

  const phoneMethod = useForm()
  const otpMethod = useForm()
  const { setValue, handleSubmit, watch } = method

  // const currentPhoneNumber = watch('phone_number')
  // const isPhoneNumberChanged = newPhoneNumber !== '' && newPhoneNumber !== initialPhoneNumber

  const updateCustomer = useMutation({
    mutationKey: ['UPDATE_CUSTOMER_INFO'],
    mutationFn: (data: any) => updateInfoCustomer(data),
    onSuccess: (data: any) => {},
    onError: () => {},
  })

  const handleUpdateCustomerInfo = async (data: yup.InferType<typeof userSchema>) => {
    const payload = {
      allow_remote_shopping_assistance: true,
      firstname: data.firstname?.trim() || ' ',
      lastname: data.lastname?.trim() || '',
      phone_number: data.phone_number?.trim() || '',
      date_of_birth: data.date_of_birth ?? '',
      is_subscribed: true,
      account_holder: data.account_holder ?? '',
      account_number: data.account_number ?? '',
      account_bank: data.account_bank ?? '',
    }

    const res = await updateCustomer.mutateAsync(payload)

    if (res?.status === 'success') {
      toast.success('Cập nhật thông tin thành công')
      await updateUser()
      // setInitialPhoneNumber(data.phone_number?.trim() || '')
    } else if (res?.status === 'error') {
      toast.error(res?.message)
    }
  }

  const onSubmit = async (data: yup.InferType<typeof userSchema>) => {
    try {
      // Nếu không thay đổi số điện thoại, update thông tin bình thường
      await handleUpdateCustomerInfo(data)
    } catch (error) {
      console.error('Error updating customer info:', error)
      toast.error('Có lỗi xảy ra khi cập nhật thông tin')
      setIsSendingOtp(false)
    }
  }

  const handleUpdatePhoneNumber = () => {
    setShowPhoneDialog(true)
    setNewPhoneNumber('')
  }

  const handleSubmitNewPhone = async () => {
    if (!newPhoneNumber || newPhoneNumber.trim() === '') {
      toast.error('Vui lòng nhập số điện thoại')
      return
    }

    try {
      setIsSendingOtp(true)

      // Gửi OTP
      const result = await requestUniversalOtp(newPhoneNumber)

      setIsSendingOtp(false)

      if (result.status === 'error') {
        toast.error(result.message || 'Có lỗi xảy ra khi gửi mã OTP')
        return
      }

      toast.success('Mã OTP đã được gửi đến số điện thoại mới của bạn')

      // Tạo pending data với số điện thoại mới
      const currentData = method.getValues()
      const dataWithNewPhone = {
        ...currentData,
        phone_number: newPhoneNumber,
      }

      setPendingData(dataWithNewPhone)
      setShowPhoneDialog(false)
      setShowOtpDialog(true)
      setOtpErrorMessage(null)
    } catch (error) {
      console.error('Error sending OTP:', error)
      toast.error('Có lỗi xảy ra khi gửi mã OTP')
      setIsSendingOtp(false)
    }
  }

  const handleClosePhoneDialog = () => {
    setShowPhoneDialog(false)
    setNewPhoneNumber('')
  }

  const handleOtpComplete = async (otpValue: string) => {
    setOtpValue(otpValue)
  }

  const handleSubmitOtp = async () => {
    if (!pendingData || otpValue.length !== 6) return

    setIsVerifyingOtp(true)
    setOtpErrorMessage(null)

    try {
      const phoneNumber = pendingData.phone_number?.trim() || ''
      const result = await verifyUniversalOtp(phoneNumber, otpValue)

      if (result.status === 'error') {
        setOtpErrorMessage(result.message || 'Mã OTP không hợp lệ')
        setIsVerifyingOtp(false)
        return
      }

      // OTP đúng, cập nhật thông tin
      await handleUpdateCustomerInfo(pendingData)
      setShowOtpDialog(false)
      setPendingData(null)
      setOtpValue('')
      otpMethod.reset()
    } catch (error) {
      console.error('Error verifying OTP:', error)
      setOtpErrorMessage('Có lỗi xảy ra khi xác thực mã OTP')
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleResendOtp = async () => {
    if (!pendingData) return

    try {
      const phoneNumber = pendingData.phone_number?.trim() || ''
      const result = await requestUniversalOtp(phoneNumber)

      if (result.status === 'error') {
        setOtpErrorMessage(result.message || 'Có lỗi xảy ra khi gửi lại mã OTP')
        return
      }

      setOtpErrorMessage(null)
      toast.success('Mã OTP đã được gửi lại')
    } catch (error) {
      console.error('Error resending OTP:', error)
      setOtpErrorMessage('Có lỗi xảy ra khi gửi lại mã OTP')
    }
  }

  const handleCloseOtpDialog = () => {
    setShowOtpDialog(false)
    setPendingData(null)
    setOtpErrorMessage(null)
    setOtpValue('')
    otpMethod.reset()
  }

  useEffect(() => {
    setValue('email', data?.email)
    setValue('firstname', data?.firstname ?? '')
    setValue('lastname', data?.lastname ?? '')
    setValue('phone_number', data?.phone_number ?? '')
    setValue('date_of_birth', data?.date_of_birth ?? '')
    setValue('account_holder', data?.account_holder ?? '')
    setValue('account_number', data?.account_number ?? '')
    setValue('account_bank', data?.account_bank ?? '')
    // setInitialPhoneNumber(data?.phone_number ?? '')
  }, [data, setValue])

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const url = await getBase64(file)
    const payload = {
      allow_remote_shopping_assistance: true,
      firstname: data.firstname?.trim() || ' ', //do magento cần firstname nên cần truyền khoảng trắng
      lastname: data.lastname?.trim() || '',
      phone_number: data.phone_number?.trim() || '',
      is_subscribed: true,
      date_of_birth: data.date_of_birth ?? '',
      profile_upload_picture: {
        name: 'file_name',
        base64_encoded_file: url,
      },
    }
    const res = await updateCustomer.mutateAsync(payload)
    if (res?.status === 'success') {
      toast.success('Cập nhật thông tin thành công')
      await updateUser()
    } else if (res?.status === 'error') {
      toast.error(res?.message)
    }
    setIsUpdateBankInfo(false)
  }
  return (
    <>
      <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Avatar
            src={data?.profile_picture || INTERNAL_DATA.avatar_default}
            sx={{ width: 160, height: 160 }}
          />
          <ButtonRoundStyle
            sx={{
              width: 'fit-content',
              gap: 0.5,
              minWidth: 140,
            }}
            variant="outlined"
          >
            <label
              htmlFor="avatarInput"
              style={{ cursor: 'pointer', color: '#7a7a7a', marginTop: 4, marginBottom: 4 }}
            >
              {t('Tải ảnh lên')}
            </label>
            <input
              type="file"
              id="avatarInput"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </ButtonRoundStyle>
        </Box>
        <Box
          sx={{
            flex: '1',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              mb: 2,
            }}
            variant="h6"
          >
            {t('Thông tin tài khoản')}
          </Typography>
          <Form onSubmit={handleSubmit(onSubmit)} methods={method}>
            <Grid container spacing={1} columns={12} sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <RHFTextField
                  name="lastname"
                  label={t('Họ và tên')}
                  placeholder={t('enter-your', { name: t('họ và tên') })}
                  sx={{ flex: 1, minWidth: 170, maxWidth: '100%' }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RHFDatePicker
                  name="date_of_birth"
                  label="Ngày sinh"
                  placeholder="Nhập ngày sinh của bạn"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ position: 'relative' }}>
                  <RHFTextField
                    name="phone_number"
                    label="Số điện thoại"
                    placeholder="Nhập số điện thoại của bạn"
                    sx={{ flex: 1, minWidth: 170, maxWidth: '100%' }}
                    disabled
                    required
                  />
                  <Box
                    onClick={handleUpdatePhoneNumber}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      bottom: '0px',
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      height: '2.5rem',
                      borderRadius: '0 99px 99px 0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip title={t('Cập nhật số điện thoại')} placement="top" arrow>
                      <Box
                        sx={{
                          width: '1.5rem',
                          height: '1.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 999,
                          cursor: 'pointer',
                          transitionDuration: '0.3s',
                          ':hover': {
                            background: '#f0f0f0',
                          },
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M12 11.3854C12 11.1566 11.8102 10.9668 11.5814 10.9668H0.418605C0.189767 10.9668 0 11.1566 0 11.3854C0 11.6142 0.189767 11.804 0.418605 11.804H11.5814C11.8102 11.804 12 11.6142 12 11.3854Z"
                            fill="#252422"
                          />
                          <path
                            d="M7.37306 0.926512L7.86423 0.435349C8.44469 -0.145116 9.45492 -0.145116 10.0354 0.435349L10.4317 0.831628C10.7219 1.12186 10.8838 1.50698 10.8838 1.91442C10.8838 2.32186 10.7219 2.71256 10.4317 2.99721L9.94051 3.48837L7.37306 0.92093V0.926512ZM6.78144 1.51814L1.69678 6.60279C1.53492 6.76465 1.43446 6.97674 1.41771 7.20558L1.26702 8.84093C1.25027 9.04744 1.32283 9.24837 1.46795 9.39907C1.6019 9.53302 1.77492 9.60558 1.95911 9.60558H2.02051L3.65585 9.45488C3.88469 9.43256 4.09678 9.33209 4.25865 9.17023L9.3433 4.08558L6.77585 1.51814H6.78144Z"
                            fill="#252422"
                          />
                        </svg>
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RHFTextField
                  name="email"
                  label="Email"
                  placeholder="example@gmail.com"
                  disabled
                  sx={{ flex: 1, minWidth: 170, maxWidth: '100%' }}
                />
              </Grid>

              <Grid container size={{ xs: 12 }} sx={{ mb: 3 }}>
                <ButtonRoundStyle
                  type="submit"
                  loading={updateCustomer.isPending && !isUpdateBankInfo}
                  sx={{
                    width: 'fit-content',
                    mt: 2,
                    gap: 0.5,
                    minWidth: 140,
                    textTransform: 'uppercase',
                  }}
                  disabled={updateCustomer.isPending && !isUpdateBankInfo}
                  variant="contained"
                >
                  {t('Lưu thông tin')}
                </ButtonRoundStyle>
              </Grid>
            </Grid>
          </Form>
          <Typography
            sx={{
              fontWeight: 500,
              mb: 2,
            }}
            variant="h6"
          >
            {t('Thông tin ngân hàng')}
          </Typography>
          <Form onSubmit={handleSubmit(onSubmit)} methods={method}>
            <Grid container spacing={1} columns={12} sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <RHFTextField
                  name="account_holder"
                  label="Tên tài khoản"
                  placeholder="Tên tài khoản"
                  sx={{ flex: 1, minWidth: 170, maxWidth: '100%' }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RHFTextField
                  name="account_number"
                  label="Số tài khoản"
                  placeholder="Số tài khoản"
                  sx={{ flex: 1, minWidth: 170, maxWidth: '100%' }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RHFAutocomplete
                  className="w-full"
                  placeholder="Chọn ngân hàng"
                  options={bankOptions}
                  label="Ngân hàng"
                  name="account_bank"
                  sx={{ flex: 1, minWidth: 170, maxWidth: '100%' }}
                />
              </Grid>
              <Grid container size={{ xs: 12 }} sx={{ mb: 3 }}>
                <ButtonRoundStyle
                  type="submit"
                  loading={updateCustomer.isPending && isUpdateBankInfo}
                  onClick={() => {
                    setIsUpdateBankInfo(true)
                  }}
                  sx={{
                    width: 'fit-content',
                    mt: 2,
                    gap: 0.5,
                    minWidth: 140,
                    textTransform: 'uppercase',
                  }}
                  disabled={updateCustomer.isPending && isUpdateBankInfo}
                  variant="contained"
                >
                  {t('Lưu thông tin')}
                </ButtonRoundStyle>
              </Grid>
            </Grid>
          </Form>
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Stack
              sx={{
                flex: '1',
              }}
              spacing={2}
            >
              <Stack spacing={0.5}>
                <Typography
                  sx={{
                    fontWeight: 500,
                  }}
                  variant="h6"
                >
                  {isFirstChangePassword ? (
                    <span>{t('Cập nhật mật khẩu lần đầu')}</span>
                  ) : (
                    <span>{t('Đổi mật khẩu')}</span>
                  )}
                </Typography>
                {isFirstChangePassword && (
                  <Typography fontSize={'0.875rem'} fontWeight={300}>
                    {t.rich(
                      'Để bảo mật tài khoản của bạn, vui lòng tạo mật khẩu mới cho lần đăng nhập tiếp theo',
                      {
                        bold: (chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>,
                      }
                    )}
                  </Typography>
                )}
              </Stack>
              {isFirstChangePassword ? <SetInitialPasswordForm haveTitle={false} /> : <ChangePassword />}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Phone Update Dialog */}
      <PopupWrapper
        isOpen={showPhoneDialog}
        width="450px"
        maxHeight="85vh"
        handleClose={handleClosePhoneDialog}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 500 }}>{t('Cập nhật số điện thoại')}</DialogTitle>
        <DialogContent sx={{}}>
          <Stack spacing={3} sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              {t('Vui lòng nhập số điện thoại mới của bạn')}. {t('Chúng tôi sẽ gửi mã OTP để xác thực')}.
            </Typography>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.5,
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {t('Số điện thoại mới')}
              </Typography>
              <input
                type="text"
                placeholder={t('Nhập số điện thoại mới')}
                value={newPhoneNumber}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPhoneNumber(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1976d2'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)'
                }}
              />
            </Box>
            <Stack spacing={2}>
              <ButtonRoundStyle
                variant="contained"
                onClick={handleSubmitNewPhone}
                disabled={!newPhoneNumber || isSendingOtp}
                loading={isSendingOtp}
                fullWidth
              >
                {t('Gửi mã OTP')}
              </ButtonRoundStyle>
              <ButtonRoundStyle
                variant="outlined"
                onClick={handleClosePhoneDialog}
                disabled={isSendingOtp}
                fullWidth
              >
                {t('Hủy')}
              </ButtonRoundStyle>
            </Stack>
          </Stack>
        </DialogContent>
      </PopupWrapper>

      {/* OTP Dialog */}
      <PopupWrapper isOpen={showOtpDialog} handleClose={handleCloseOtpDialog} width="540px" maxHeight="85vh">
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 500 }}>{t('Xác thực số điện thoại')}</DialogTitle>
        <DialogContent>
          <Form methods={otpMethod} onSubmit={otpMethod.handleSubmit(() => {})}>
            <Stack spacing={3} sx={{ py: 1 }}>
              <OTPCountDown
                onSubmit={handleOtpComplete}
                align="center"
                subtitle={t.rich(
                  'Vui lòng nhập đoạn mã gồm 6 chữ số vừa gửi đến số điện thoại {goal} để xác minh',
                  {
                    goal: pendingData?.phone_number || '',
                    bold: (chunks) => <strong>{chunks}</strong>,
                  }
                )}
                onResendOTP={handleResendOtp}
                errorMessage={otpErrorMessage}
              />
              <Stack spacing={2}>
                <ButtonRoundStyle
                  variant="contained"
                  onClick={handleSubmitOtp}
                  disabled={otpValue.length !== 6 || isVerifyingOtp}
                  loading={isVerifyingOtp}
                  fullWidth
                >
                  {t('Xác nhận')}
                </ButtonRoundStyle>
                <ButtonRoundStyle
                  variant="outlined"
                  onClick={handleCloseOtpDialog}
                  disabled={isVerifyingOtp}
                  fullWidth
                >
                  {t('Hủy')}
                </ButtonRoundStyle>
              </Stack>
            </Stack>
          </Form>
        </DialogContent>
      </PopupWrapper>
    </>
  )
}
